<template>
  <div class="login-container mobile-login">
    <div class="login-card">
      <div class="login-header">
        <h2>教师登录</h2>
      </div>
      
      <el-form :model="loginForm" :rules="loginRules" ref="loginForm" class="login-form">
        <el-form-item prop="phone">
          <el-input 
            v-model="loginForm.phone" 
            placeholder="请输入手机号"
            maxlength="11">
          </el-input>
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input 
            v-model="loginForm.password" 
            type="password" 
            placeholder="请输入密码"
            show-password
            @keyup.enter.native="handleLogin">
          </el-input>
        </el-form-item>
        
        <!-- 智能验证码区域 -->
        <el-form-item v-if="showCaptcha" prop="captcha">
          <div class="captcha-container">
            <el-input 
              v-model="loginForm.captcha" 
              placeholder="请输入验证码"
              @keyup.enter.native="handleLogin">
            </el-input>
            <img 
              :src="captchaImage" 
              @click="refreshCaptcha"
              class="captcha-image"
              alt="验证码">
          </div>

        </el-form-item>
        

        
        <el-form-item>
          <el-button 
            type="primary" 
            @click="handleLogin" 
            :loading="loading" 
            class="full-width-btn">登录</el-button>
        </el-form-item>
      </el-form>
      
      <div class="register-link">
        <span>没有账号？</span>
        <el-button type="text" @click="goToRegister">快速注册</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginMobile',
  mounted() {
    // 确保登录页面不受任何主题类的影响
    document.body.classList.remove('chinese-theme', 'math-theme', 'english-theme');
    // 添加移动端样式类
    document.body.classList.add('mobile-view');
  },
  beforeDestroy() {
    // 移除移动端样式类
    document.body.classList.remove('mobile-view');
  },
  data() {
    return {
      loading: false,
      showCaptcha: false,
      captchaImage: '',
      captchaUuid: '',
      loginForm: {
        phone: '',
        password: '',
        captcha: ''
      },
      loginRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        captcha: []
      },
      redirectUrl: null
    }
  },
  created() {
    this.redirectUrl = this.$route.query.redirect || null;
  },
  methods: {
    // 手机号输入完成后检查是否需要验证码
    async onPhoneInput() {
      if (this.loginForm.phone && this.loginForm.phone.length === 11) {
        await this.checkCaptchaRequirement(this.loginForm.phone);
      }
    },
    
    // 检查是否需要验证码
    async checkCaptchaRequirement(phone) {
      try {
        const response = await this.$http.get('/teachers/check-captcha-required', {
          params: { phone }
        });
        
        this.showCaptcha = response.data.captchaRequired;
        
        if (this.showCaptcha) {
          await this.refreshCaptcha();
          // 动态添加验证码验证规则
          this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
        } else {
          this.captchaHint = '';
          this.$set(this.loginRules, 'captcha', []);
          // 清空验证码相关数据
          this.loginForm.captcha = '';
          this.captchaImage = '';
        }
      } catch (error) {
        console.error('检查验证码需求失败:', error);
      }
    },
    
    // 刷新验证码
    async refreshCaptcha() {
      this.captchaUuid = this.generateUUID();
      try {
        const response = await this.$http.get(`/teachers/captcha-image/${this.captchaUuid}`);
        this.captchaImage = response.data.captcha.image;
      } catch (error) {
        console.error('刷新验证码失败:', error);
        this.$message.error('验证码加载失败');
      }
    },
    
    // 生成UUID
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate();
        this.loading = true
        
        // 生成会话ID
        const sessionId = this.generateSessionId();
        
        const requestData = {
          phone: this.loginForm.phone,
          password: this.loginForm.password,
          deviceType: 'mobile', // 移动端登录
          sessionId: sessionId
        };
        
        // 如果显示验证码，则添加验证码信息
        if (this.showCaptcha) {
          requestData.captchaUuid = this.captchaUuid;
          requestData.captchaCode = this.loginForm.captcha;
        }
        
        const response = await this.$http.post('/teachers/login', requestData)
        
        if (response.data.success) {
          // 登录成功，清空验证码相关状态
          this.showCaptcha = false;
          this.loginForm.captcha = '';
          
          // 保存JWT、会话ID和用户信息到本地存储
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('sessionId', response.data.sessionId); // 保存服务器返回的会话ID
          localStorage.setItem('deviceType', 'mobile'); // 记录设备类型
          localStorage.setItem('teacherInfo', JSON.stringify(response.data.teacher))
          
          if (this.redirectUrl) {
            this.$router.push(this.redirectUrl);
          } else if (response.data.hasClasses) {
            // 获取教师绑定的班级信息（使用新的安全接口）
            const boundClassesResponse = await this.$http.get('/teachers/bound-classes');
            
            if (boundClassesResponse.data.success) {
              const boundClasses = boundClassesResponse.data.boundClasses;
              
              if (boundClasses.length === 1) {
                // 如果只有一个班级，直接跳转到移动端该班级的主页
                const classInfo = boundClasses[0];
                this.$router.push(`/mobile/class/${classInfo.classId}?subjectId=${classInfo.subjectId}`);
              } else {
                // 如果有多个班级或没有班级，跳转到/mobile界面
                this.$router.push('/mobile');
              }
            } else {
              // 如果获取班级信息失败，跳转到/mobile界面
              this.$router.push('/mobile');
            }
          } else {
            // 如果没有班级，跳转到/mobile界面
            this.$router.push('/mobile');
          }
          
          this.$message.success(response.data.message)
        } else {
          // 登录失败处理
          this.$message.error(response.data.message);
          
          // 更新验证码状态
          if (response.data.captchaRequired) {
            this.showCaptcha = true;
            this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
            await this.refreshCaptcha();
            this.loginForm.captcha = '';
          } else {
            this.showCaptcha = false;
            this.$set(this.loginRules, 'captcha', []);
            this.loginForm.captcha = '';
          }
          

        }
      } catch (error) {
        // 区分表单验证错误和其他错误
        // Element UI表单验证失败会抛出包含字段错误信息的对象
        if (error.constructor.name === 'Error' && error.message.includes('validate')) {
          // 表单验证错误已经在Element UI层面处理了，不需要额外提示
          return;
        }
        
        // 检查是否是对象形式的表单验证错误
        if (typeof error === 'object' && error !== null && (error.captcha || error.phone || error.password)) {
          // 这些错误已经在Element UI层面通过红色提示显示了，不需要额外处理
          return;
        }
        
        // 检查是否是验证码相关的错误
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message);
          // 如果后端提示需要验证码，显示验证码输入框
          if (error.response.data.captchaRequired) {
            this.showCaptcha = true;
            this.$set(this.loginRules, 'captcha', [{ required: true, message: '请输入验证码', trigger: 'blur' }]);
            await this.refreshCaptcha();
            this.loginForm.captcha = '';
          }
        } else {
          this.$message.error('登录失败，请重试');
        }
      } finally {
        this.loading = false
      }
    },
    
    // 生成随机会话ID
    generateSessionId() {
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },
    
    goToRegister() {
      this.$router.push('/signinmobile')
    }
  },
  watch: {
    // 监听手机号变化
    'loginForm.phone'(newPhone) {
      if (newPhone && newPhone.length === 11) {
        this.checkCaptchaRequirement(newPhone);
      }
    }
  }
}
</script>

<style scoped>
.login-container.mobile-login {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #409EFF, #64b5f6);
}

.login-card {
  width: 100%;
  max-width: 350px;
  background: white;
  border-radius: 15px;
  padding: 30px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h2 {
  color: #303133;
  font-size: 24px;
  margin: 0;
  font-weight: bold;
}

.login-form {
  margin-bottom: 20px;
}

.el-form-item {
  margin-bottom: 20px;
}

.el-input__inner {
  height: 45px;
  line-height: 45px;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  padding: 0 15px;
  font-size: 14px;
}

.full-width-btn {
  width: 100%;
  height: 45px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
}

.register-link {
  text-align: center;
}

.register-link span {
  color: #909399;
  font-size: 14px;
}

.el-button--text {
  color: #409EFF;
  font-size: 14px;
}

/* 移动端适配 */
@media screen and (max-width: 480px) {
  .login-container.mobile-login {
    padding: 10px;
  }
  
  .login-card {
    margin: 10px;
    padding: 25px 15px;
  }
  
  .login-header h2 {
    font-size: 22px;
  }
  
  .el-input__inner {
    height: 42px;
    line-height: 42px;
    font-size: 14px;
  }
  
  .full-width-btn {
    height: 42px;
    font-size: 15px;
  }
}

/* 验证码样式 */
.captcha-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.captcha-container .el-input {
  flex: 1;
}

.captcha-image {
  height: 40px;
  width: 100px;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: all 0.3s;
}

.captcha-image:hover {
  border-color: #409EFF;
  transform: scale(1.05);
}




</style>